import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = {
    class: "bg-primary-25 flex flex-col text-left justify-center px-4 text-gray-900 text-base font-medium",
    style: { "height": "52px" }
};
const _hoisted_2 = {
    style: { "height": "498px" },
    class: "flex flex-col space-y-3 overflow-y-scroll py-2 px-3"
};
const _hoisted_3 = { class: "flex flex-wrap justify-between" };
const _hoisted_4 = ["onClick"];
const _hoisted_5 = {
    class: "flex flex-col rounded-lg space-x-2 items-center justify-center",
    style: { "width": "67px", "height": "67px" }
};
import { cropOptions } from '@/compositions/useCropOptions';
import { Identifiers } from '@/types/identifiers';
import { Tools } from '@/types/tools';
import { UIDivider, UITextXsMedium, UITextXsRegular } from '@gohighlevel/ghl-ui';
import Konva from 'konva';
import { computed, ref, watch } from 'vue';
export default /*#__PURE__*/ _defineComponent({
    __name: 'CropTool',
    props: {
        baseImage: {
            type: Object,
            require: true,
        },
        stage: {
            type: Object,
            require: true,
        },
        group: {
            type: Object,
            require: true,
        },
        reset: {
            type: Boolean,
            require: true,
        },
        selectedOption: {
            type: String,
            require: true,
        },
        loading: {
            type: Boolean,
            require: true,
        },
    },
    setup(__props) {
        const props = __props;
        let cropBox, cropBoxTransform;
        const cropStyle = ref('custom');
        const aspectRatio = ref();
        const changed = ref(false);
        const cropBoxAdded = ref(false);
        const activeTool = computed(() => props.selectedOption);
        const isLoading = computed(() => props.loading);
        const isReset = computed(() => props.reset);
        watch(isReset, () => {
            cropStyle.value = '';
            aspectRatio.value = null;
            changed.value = false;
            cropBoxTransform = undefined;
            cropBox = undefined;
        });
        watch(isLoading, () => {
            if (!isLoading.value && props.selectedOption === Tools.CROP) {
                initializeCropping();
            }
        });
        watch(activeTool, () => {
            if (activeTool.value === Tools.CROP) {
                initializeCropping();
            }
            else {
                removeCropBox();
            }
        });
        function initializeCropping() {
            removeCropBox();
            cropStyle.value = 'custom';
            aspectRatio.value = undefined;
            addCropBox();
            cropBoxAdded.value = true;
        }
        function removeCropBox() {
            cropBoxTransform = props.group.find(`#${Identifiers.CROP_BOX_TRANSFORMER}`)[0];
            cropBox = props.group.find(`#${Identifiers.CROP_BOX}`)[0];
            if (cropBoxTransform)
                cropBoxTransform.destroy();
            if (cropBox)
                cropBox.destroy();
            props.stage.batchDraw();
            cropBoxAdded.value = false;
        }
        function addCropBox() {
            let height = props.baseImage.height();
            if (props.baseImage.rotation() !== 0 &&
                (props.baseImage.rotation() / 2) % 2 !== 0) {
                height = props.baseImage.width();
            }
            const { cropWidth, cropHeight } = getCropBoxWidthHeight(height);
            cropBox = new Konva.Rect({
                x: props.baseImage.x(),
                y: props.baseImage.y(),
                width: cropWidth,
                height: cropHeight,
                draggable: true,
                keepRatio: true,
                id: Identifiers.CROP_BOX,
            });
            // Set up a dragstart event handler
            cropBox.on('dragstart', () => {
                // Store the initial position of the rectangle
                cropBox.startX = cropBox.x();
                cropBox.startY = cropBox.y();
            });
            // Set up a dragmove event handler
            cropBox.on('dragmove', () => {
                checkCropBoxBoundary();
            });
            props.group.add(cropBox);
            cropBoxTransform = new Konva.Transformer({
                rotateEnabled: false,
                draggable: true,
                id: Identifiers.CROP_BOX_TRANSFORMER,
            });
            cropBoxTransform.nodes([cropBox]);
            props.group.add(cropBoxTransform);
            props.stage.batchDraw();
            registerEvents();
        }
        function registerEvents() {
            cropBoxTransform.boundBoxFunc(function (oldBox, newBox) {
                if (newBox.width > props.stage.width() ||
                    newBox.height > props.stage.height() ||
                    (aspectRatio.value &&
                        (oldBox.width == newBox.width || oldBox.height == newBox.height))) {
                    return oldBox;
                }
                return newBox;
            });
            cropBox.on('mouseover', () => {
                document.body.style.cursor = 'move';
            });
            cropBox.on('mouseout', () => {
                document.body.style.cursor = 'default';
            });
        }
        function getCropBoxWidthHeight(height, reducingFactor) {
            let cropWidth = props.baseImage.width();
            let cropHeight = props.baseImage.height();
            if (aspectRatio.value) {
                const r = aspectRatio.value;
                cropWidth = height * r;
                cropHeight = cropWidth / r;
            }
            if (cropWidth > props.baseImage.width()) {
                reducingFactor = reducingFactor ? reducingFactor + 100 : 100;
                const adjustWidthHeight = getCropBoxWidthHeight(props.baseImage.height() - reducingFactor, reducingFactor);
                cropWidth = adjustWidthHeight.cropWidth;
                cropHeight = adjustWidthHeight.cropHeight;
            }
            return { cropWidth, cropHeight };
        }
        function checkCropBoxBoundary() {
            // Check if the rectangle is about to go outside the canvas boundaries
            if (cropBox.x() < 0) {
                cropBox.x(0); // Prevent moving left of canvas
            }
            if (cropBox.y() < 0) {
                cropBox.y(0); // Prevent moving above canvas
            }
            if (cropBox.x() + cropBoxTransform.width() > props.stage.width()) {
                cropBox.x(props.stage.width() - cropBoxTransform.width()); // Prevent moving right of canvas
            }
            if (cropBox.y() + cropBoxTransform.height() > props.stage.height()) {
                cropBox.y(props.stage.height() - cropBoxTransform.height()); // Prevent moving below canvas
            }
        }
        function setCropFrame(option) {
            cropStyle.value = option.value;
            aspectRatio.value = option.aspectRatio;
            if (!cropBox && !cropBoxTransform) {
                addCropBox();
            }
            else {
                const { cropWidth, cropHeight } = getCropBoxWidthHeight(props.baseImage.height());
                const left = Math.floor(props.stage.width() / 2) -
                    Math.floor(props.baseImage.width() / 2);
                const top = Math.floor(props.stage.height() / 2) -
                    Math.floor(props.baseImage.height() / 2);
                cropBox.setAttrs({
                    x: left,
                    y: top,
                    width: cropWidth,
                    height: cropHeight,
                });
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("div", _hoisted_1, _toDisplayString(_ctx.$t('crop.title')), 1),
                _createElementVNode("div", _hoisted_2, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(cropOptions), (cropType) => {
                        return (_openBlock(), _createElementBlock("div", {
                            key: cropType.value,
                            class: "flex flex-col space-y-2"
                        }, [
                            _createVNode(_unref(UITextXsMedium), { class: "text-gray-900" }, {
                                default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_ctx.$t(cropType.title)), 1)
                                ]),
                                _: 2
                            }, 1024),
                            _createElementVNode("div", _hoisted_3, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cropType.options, (option) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: option.title,
                                        style: { "height": "120px", "width": "110px" },
                                        class: _normalizeClass(["flex flex-col items-center cursor-pointer justify-center rounded-lg border mb-2",
                                            option.value == cropStyle.value ? 'border-primary' : 'border-gray-300'
                                        ]),
                                        onClick: ($event) => (setCropFrame(option))
                                    }, [
                                        _createElementVNode("div", _hoisted_5, [
                                            _createElementVNode("div", {
                                                class: _normalizeClass(["p-2 flex flex-col items-center justify-center text-gray-700 font-bold text-xs rounded-lg", `bg-gray-200 border border-gray-400 border-dashed`]),
                                                style: _normalizeStyle(option.styles)
                                            }, _toDisplayString(option.placeholder), 5)
                                        ]),
                                        _createVNode(_unref(UITextXsRegular), { class: "text-gray-700" }, {
                                            default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_ctx.$t(option.title)), 1)
                                            ]),
                                            _: 2
                                        }, 1024)
                                    ], 10, _hoisted_4));
                                }), 128))
                            ]),
                            _createVNode(_unref(UIDivider))
                        ]));
                    }), 128))
                ])
            ]));
        };
    }
});
